/**
 * Presentation helpers for a file document's metadata.
 *
 * The details modal needs the same handful of facts in three different shapes —
 * a header summary strip, a technical-details list, and tooltips — and every
 * host used to format them inline (size in MB here, raw ISO strings there). One
 * module so "0.30 MB" and "1 Jul 2026, 14:11" mean the same thing everywhere.
 *
 * Leaf module: imports only from utils/format so any layer can consume it.
 */

import { fmtBytes, extensionOf } from './format';

export { fmtBytes, extensionOf };

/**
 * Timestamps reach the frontend in three shapes depending on the path they took
 * (Firestore `{_seconds}`, epoch ms from a cached list, an ISO string from the
 * REST layer). Normalize once here rather than at every call site.
 */
export function toDate(raw) {
    if (raw == null || raw === '') return null;
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
    if (typeof raw === 'object') {
        const seconds = raw._seconds ?? raw.seconds;
        return typeof seconds === 'number' ? new Date(seconds * 1000) : null;
    }
    const date = new Date(typeof raw === 'number' ? raw : String(raw));
    return Number.isNaN(date.getTime()) ? null : date;
}

/** "1 Jul 2026" */
export function fmtDate(raw) {
    const date = toDate(raw);
    if (!date) return null;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "1 Jul 2026, 14:11" — the full form, used for tooltips and the detail list. */
export function fmtDateTime(raw) {
    const date = toDate(raw);
    if (!date) return null;
    return date.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

/** "0:03", "4:07", "1:02:03" — clock form, matching the player's readout. */
export function fmtDuration(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return null;
    const total = Math.round(n);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (v) => String(v).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "320 kbps" / "1.4 Mbps" */
export function fmtBitrate(bitsPerSecond) {
    const n = Number(bitsPerSecond);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} Mbps`;
    return `${Math.round(n / 1000)} kbps`;
}

/** "1920 × 1080" */
export function fmtDimensions(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return `${w} × ${h}`;
}

/**
 * Short badge label — "WAV", "PNG", "PDF". The extension is what people call the
 * file, so it wins; the MIME subtype is the fallback for extensionless uploads.
 */
export function typeLabel(mimetype = '', name = '') {
    const ext = extensionOf(name);
    if (ext) return ext.toUpperCase();
    const subtype = String(mimetype).split('/')[1];
    if (subtype) return subtype.split(/[+.;]/)[0].toUpperCase();
    return 'FILE';
}

/**
 * Formats ModelViewer can load, so the 3D preview — and with it the "Capture
 * Thumbnail" button that overrides the server-rendered one — is offered for all
 * of them. The backend thumbnails a wider set (stl, ply) that the viewer has no
 * loader for; those still get a rendered tile, just no in-app override.
 */
export const VIEWABLE_MODEL_EXTENSIONS = ['fbx', 'obj', 'gltf', 'glb'];

/** True when shared/FilePreview.jsx will hand this file to the 3D viewer. */
export function isViewableModel(name = '') {
    return VIEWABLE_MODEL_EXTENSIONS.indexOf(extensionOf(name)) >= 0;
}

/**
 * Coarse category driving the placeholder icon and which tools apply.
 * Mirrors the switch in shared/FilePreview.jsx.
 */
export function kindOf(mimetype = '', name = '') {
    const lower = String(name).toLowerCase();
    const mime = String(mimetype);
    if (mime === 'image/x-exr' || lower.endsWith('.exr')) return 'image';
    if (isViewableModel(name)) return 'model';
    if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf';
    const top = mime.split('/')[0];
    if (top === 'audio' || top === 'video' || top === 'image' || top === 'text') return top;
    return 'file';
}

/**
 * What ROLE a file plays in the authoring pipeline, as opposed to what kind of
 * file it is.
 *
 * Deliberately separate from `kindOf`, which decides the icon and the preview: a
 * `.blend` is still a document, and swapping its icon would lose that. What the
 * badge adds is that this particular document is somebody's source file — which,
 * for a format nothing can render a preview of, is the only thing distinguishing
 * its tile from a `.zip`.
 *
 * Returns null for the overwhelming majority of files, which have no links and
 * no stage and should carry no extra chrome at all.
 */
export function roleOf(file) {
    const stage = file?.stage ?? null;
    const derivation = file?.derivation ?? null;
    const exportCount = derivation?.exportCount ?? 0;
    const sourceCount = derivation?.sourceCount ?? 0;

    if (stage === 'source' || stage === 'raw') return stage;
    // No stage set, but things were exported from it — that IS a source file,
    // whether or not anyone got round to classifying it.
    if (exportCount > 0) return 'source';
    if (stage === 'export' || sourceCount > 0) return 'export';
    return null;
}

/** How many versions behind this file's sources it is; 0 when fresh or unknown. */
export function staleByOf(file) {
    const n = Number(file?.derivation?.staleBy);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** How many files made from this one are behind it. */
export function staleExportsOf(file) {
    const n = Number(file?.derivation?.staleExports);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Whisper (audio/video) and the vision describer (images) — nothing else. */
export function canTranscribe(mimetype = '') {
    const top = String(mimetype).split('/')[0];
    return top === 'audio' || top === 'video' || top === 'image';
}

/** `folderPath` → "Folder path"; `videoCodec` → "Video codec". */
export function humanizeKey(key) {
    const spaced = String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
    if (!spaced) return String(key);
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Probed/derived metadata, in reading order. These are facts ABOUT the bytes —
 * ffprobe output, the storage layer's own bookkeeping, the import hierarchy —
 * so they are shown, never offered as free-text fields. Editing `duration` or
 * `folderPath` by hand only ever produced a document that disagreed with the
 * file it describes.
 */
export const TECHNICAL_FIELDS = [
    { key: 'mimetype', label: 'MIME type' },
    { key: 'size', label: 'Size', format: (v) => fmtBytes(Number(v)) },
    { key: 'duration', label: 'Duration', format: fmtDuration },
    { key: 'width', label: 'Dimensions', format: (_v, meta) => fmtDimensions(meta.width, meta.height) },
    { key: 'fps', label: 'Frame rate', format: (v) => (Number(v) > 0 ? `${Number(v)} fps` : null) },
    { key: 'bitrate', label: 'Bitrate', format: fmtBitrate },
    { key: 'videoCodec', label: 'Video codec' },
    { key: 'colorSpace', label: 'Color space' },
    { key: 'encoding', label: 'Encoding' },
    { key: 'folderPath', label: 'Folder path' },
    { key: 'timeOfCreation', label: 'Added', format: fmtDateTime },
];

/** Every key TECHNICAL_FIELDS is responsible for (`height` rides with `width`). */
export const TECHNICAL_KEYS = [...TECHNICAL_FIELDS.map((f) => f.key), 'height'];

/**
 * Resolve TECHNICAL_FIELDS against a metadata object, dropping anything absent
 * so a .wav never shows an empty "Color space" row.
 */
export function technicalRows(metadata = {}) {
    return TECHNICAL_FIELDS.reduce((rows, field) => {
        const raw = metadata[field.key];
        if (raw === undefined || raw === null || raw === '') return rows;
        const value = field.format ? field.format(raw, metadata) : String(raw);
        if (value === null || value === undefined || value === '') return rows;
        rows.push({ key: field.key, label: field.label, value });
        return rows;
    }, []);
}

/**
 * The one-line identity strip under the title: what it is, how big, how long,
 * which version, when it arrived. Ordered by how often someone needs it.
 */
export function headerFacts(file = {}) {
    const metadata = file.metadata || {};
    const facts = [];

    facts.push({ key: 'type', value: typeLabel(metadata.mimetype, metadata.name), title: metadata.mimetype || undefined });

    const size = Number(metadata.size);
    if (Number.isFinite(size) && size > 0) facts.push({ key: 'size', value: fmtBytes(size) });

    const duration = fmtDuration(metadata.duration);
    if (duration) facts.push({ key: 'duration', value: duration });

    const dimensions = fmtDimensions(metadata.width, metadata.height);
    if (dimensions) facts.push({ key: 'dimensions', value: dimensions });

    const version = Number(file.versionNumber);
    if (Number.isFinite(version) && version > 0) facts.push({ key: 'version', value: `v${version}` });

    const added = fmtDate(metadata.timeOfCreation);
    if (added) facts.push({ key: 'added', value: added, title: fmtDateTime(metadata.timeOfCreation) });

    return facts;
}
